import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCurrency } from '../context/CurrencyContext';
import { getToken } from '../api/auth';
import { ArrowLeft, ShoppingBag, MapPin, CreditCard, Package, CheckCircle, Clock, Truck, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getOrder = async (id) => {
  const res = await fetch(`${API}/orders/${id}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.order;
};

// ── Status config ────────────────────────────────────────────
const STATUS_CONFIG = {
  pending:   { color: '#8A7E72', bg: 'rgba(138,126,114,0.1)', icon: Clock,        label: 'Pending'   },
  paid:      { color: '#2E7D32', bg: 'rgba(46,125,50,0.1)',   icon: CheckCircle,  label: 'Paid'      },
  shipped:   { color: '#B08D57', bg: 'rgba(176,141,87,0.12)', icon: Truck,        label: 'Shipped'   },
  delivered: { color: '#2E7D32', bg: 'rgba(46,125,50,0.1)',   icon: Package,      label: 'Delivered' },
  cancelled: { color: '#C0392B', bg: 'rgba(192,57,43,0.1)',   icon: XCircle,      label: 'Cancelled' },
};

const getStatus = (status = '') => {
  const key = status.toLowerCase();
  return STATUS_CONFIG[key] || STATUS_CONFIG['pending'];
};

// ── Order timeline steps ─────────────────────────────────────
const TIMELINE = [
  { key: 'confirmed', label: 'Order Confirmed'  },  
  { key: 'paid',      label: 'Payment Confirmed' },
  { key: 'shipped',   label: 'Shipped'           },
  { key: 'delivered', label: 'Delivered'         },
];

const getTimelineStep = (status = '') => {
  const s = status.toLowerCase();
  if (s === 'cancelled') return -1;
  if (s === 'paid')      return 1; // ← both step 0 and 1 marked done
  if (s === 'shipped')   return 2;
  if (s === 'delivered') return 3;
  return 0;
};


const getTrackingUrl = (partner, trackingNumber) => {
  if (!trackingNumber) return null;
  if (partner === 'DTDC') {
    return `https://track.aftership.com/dtdc/${trackingNumber}`;
  }
  if (partner === 'India Post') {
    return `https://track.aftership.com/india-post/${trackingNumber}`;
  }
  return null;
};

// ── Copy to clipboard ────────────────────────────────────────
const copyToClipboard = (text, setCopied) => {
  navigator.clipboard.writeText(text).then(() => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  });
};

// ── Tracking Card component ──────────────────────────────────
const TrackingCard = ({ partner, type, trackingNumber, trackingUrl }) => {
  const [copied, setCopied] = useState(false);
  const url = trackingUrl || getTrackingUrl(partner, trackingNumber);
  const isIndiaPost = partner === 'India Post';

  return (
    <motion.div
      className="od-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.12 }}
      style={{ borderColor: 'rgba(176,141,87,0.25)' }}
    >
      <p className="od-card-title">
        <Truck size={14} style={{ color: '#B08D57' }} /> Shipment Tracking
      </p>

      {/* Partner + Type */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1C1A18' }}>
          {partner}
        </div>
        {type && (
          <span style={{
            fontSize: 9, fontWeight: 600, letterSpacing: '0.12em',
            textTransform: 'uppercase', padding: '2px 8px',
            background: type === 'Express' ? 'rgba(28,26,24,0.07)' : 'rgba(176,141,87,0.1)',
            color: type === 'Express' ? '#5C5247' : '#9A7A40',
          }}>
            {type}
          </span>
        )}
      </div>

      {/* Tracking number row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: '#FAF8F4', border: '1px solid rgba(176,141,87,0.18)',
        padding: '10px 14px', marginBottom: 14,
      }}>
        <div style={{ flex: 1 }}>
          <p style={{
            fontSize: 9, fontWeight: 600, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: '#8A7E72', marginBottom: 4,
          }}>
            {isIndiaPost ? 'Consignment Number' : 'Tracking Number'}
          </p>
          <p style={{
            fontFamily: 'monospace', fontSize: 15, fontWeight: 700,
            color: '#1C1A18', letterSpacing: '0.08em',
          }}>
            {trackingNumber}
          </p>
        </div>
        <button
          onClick={() => copyToClipboard(trackingNumber, setCopied)}
          style={{
            background: copied ? 'rgba(46,125,50,0.08)' : 'rgba(176,141,87,0.08)',
            border: `1px solid ${copied ? 'rgba(46,125,50,0.25)' : 'rgba(176,141,87,0.25)'}`,
            color: copied ? '#2E7D32' : '#B08D57',
            fontFamily: "'Jost', sans-serif",
            fontSize: 9, fontWeight: 600, letterSpacing: '0.16em',
            textTransform: 'uppercase', padding: '7px 14px',
            cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
          }}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>

      

      {/* Track button */}
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: '#1C1A18', color: '#FAF8F4',
            fontFamily: "'Jost', sans-serif",
            fontSize: 10, fontWeight: 600, letterSpacing: '0.18em',
            textTransform: 'uppercase', padding: '12px 22px',
            textDecoration: 'none', transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#B08D57'}
          onMouseLeave={e => e.currentTarget.style.background = '#1C1A18'}
        >
          Track on {partner}
        </a>
      )}
    </motion.div>
  );
};

const OrderDetailPage = () => {
  const { id } = useParams();
  const { formatPrice } = useCurrency();
  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    getOrder(id)
      .then(setOrder)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  /* ── Loading ── */
  if (loading) return (
    <>
      <style>{styles}</style>
      <div className="od-page">
        <div className="od-loading">
          <div className="od-loading-ring" />
          <p className="od-loading-text">Loading order details…</p>
        </div>
      </div>
    </>
  );

  /* ── Error ── */
  if (error) return (
    <>
      <style>{styles}</style>
      <div className="od-page">
        <div className="od-error">
          <p className="od-error-icon">✦</p>
          <p className="od-error-msg">{error}</p>
          <Link to="/account" className="od-back-link">← Back to Account</Link>
        </div>
      </div>
    </>
  );

  if (!order) return null;

  const sc          = getStatus(order.status);
  const StatusIcon  = sc.icon;
  const items       = Array.isArray(order.items) ? order.items : [];
  const timelineStep = getTimelineStep(order.status);
  const isCancelled  = order.status?.toLowerCase() === 'cancelled';

  const subtotal = items.reduce((s, i) => s + Number(i.price) * Number(i.qty), 0);
  const shipping = Number(order.total) - subtotal >= 0
    ? Number(order.total) - subtotal > 99 ? 0 : (Number(order.total) - subtotal)
    : 0;

  const date = new Date(order.created_at).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  const time = new Date(order.created_at).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <>
      <style>{styles}</style>
      <div className="od-page">

        {/* ── Hero band ── */}
        <div className="od-hero">
          <div className="od-hero-inner">
            

            <motion.div
              className="od-hero-content"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.08 }}
            >
              <p className="od-eyebrow">Order Details</p>
              <h1 className="od-order-num">
                {items.length > 0
                  ? items.map(i => i.name).join(', ').slice(0, 55) +
                    (items.map(i => i.name).join(', ').length > 55 ? '…' : '')
                  : `#${order.order_number}`}
              </h1>
              <p style={{ fontSize: 11, color: 'rgba(176,141,87,0.6)', letterSpacing: '0.1em', marginTop: 4 }}>
                #{order.order_number}
              </p>
              <p className="od-date">{date} · {time}</p>
            </motion.div>

            {/* Status pill */}
            <motion.div
              className="od-status-pill"
              style={{ background: sc.bg, color: sc.color, borderColor: sc.color + '30' }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <StatusIcon size={13} />
              {sc.label}
            </motion.div>
          </div>
        </div>

        <div className="od-body">

        

          {/* ── Tracking ── */}
          {order.tracking_number && (
            <TrackingCard
              partner={order.delivery_partner}
              type={order.delivery_type}
              trackingNumber={order.tracking_number}
              trackingUrl={order.tracking_url}
            />
          )}

          {/* ── Timeline ── */}
          {!isCancelled && (
            <motion.div
              className="od-card od-timeline-card"
              
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <p className="od-card-title">Order Progress</p>
              <div className="od-timeline">
                {TIMELINE.map((step, i) => {
                  const done    = i <= timelineStep;
                  const current = i === timelineStep;
                  return (
                    <div key={step.key} className="od-timeline-step">
                      <div className={`od-tl-dot ${done ? 'done' : ''} ${current ? 'current' : ''}`}>
                        {done && <div className="od-tl-check">✓</div>}
                      </div>
                      {i < TIMELINE.length - 1 && (
                        <div className={`od-tl-line ${i < timelineStep ? 'done' : ''}`} />
                      )}
                      <p className={`od-tl-label ${done ? 'done' : ''}`}>{step.label}</p>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* ── Items ── */}
          <motion.div
            className="od-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.22 }}
          >
            <p className="od-card-title">
              Items Ordered
              <span className="od-card-title-count">{items.length} item{items.length !== 1 ? 's' : ''}</span>
            </p>

            <div className="od-items">
              {items.map((item, idx) => (
                <motion.div
                  key={idx}
                  className="od-item"
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.28 + idx * 0.07 }}
                >
                  <div className="od-item-img">
                    {item.image
                      ? <img src={item.image} alt={item.name} />
                      : <ShoppingBag size={20} style={{ color: '#B08D57', opacity: 0.4 }} />
                    }
                  </div>
                  <div className="od-item-info">
                    <p className="od-item-name">{item.name}</p>
                    <div className="od-item-meta">
                      <span>Qty: {item.qty}</span>
                      {item.size  && <span>Size: {item.size}</span>}
                      {item.color && <span>{item.color}</span>}
                    </div>
                  </div>
                  <div className="od-item-price">
                    <p className="od-item-unit">{formatPrice(item.price)}</p>
                    {item.qty > 1 && (
                      <p className="od-item-total">
                        {formatPrice(item.price * item.qty)}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ── Bottom row: Summary + Address ── */}
          <div className="od-bottom-row">

            {/* Order Summary */}
            <motion.div
              className="od-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <p className="od-card-title">
                <CreditCard size={14} style={{ color: '#B08D57' }} /> Order Summary
              </p>
              <div className="od-summary-rows">
                <div className="od-summary-row">
                  <span>Subtotal</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="od-summary-row">
                  <span>Shipping</span>
                  <span style={{ color: shipping === 0 ? '#2E7D32' : 'inherit' }}>
                    {shipping === 0 ? 'FREE' : formatPrice(shipping)}
                  </span>
                </div>
                <div className="od-summary-row od-summary-total">
                  <span>Total Paid</span>
                  <span>{formatPrice(order.total)}</span>
                </div>
              </div>
              <div className="od-payment-tag">
              <CreditCard size={11} />
              Paid via {{
                card:        'Card',
                netbanking:  'Net Banking',
                upi:         'UPI',
                wallet:      'Wallet',
                emi:         'EMI',
              }[order.payment_method?.toLowerCase()] || 'Card'}
            </div>
            </motion.div>

            {/* Delivery Address */}
            {order.address_id && (
              <motion.div
                className="od-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.36 }}
              >
                <p className="od-card-title">
                  <MapPin size={14} style={{ color: '#B08D57' }} /> Delivery Address
                </p>
                <AddressBlock addressId={order.address_id} />
              </motion.div>
            )}
          </div>

          {/* ── Ornament ── */}
          <motion.div
            className="od-ornament"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.35 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="od-orn-line" />
            <div className="od-orn-diamond" />
            <div className="od-orn-line od-orn-line--r" />
          </motion.div>

        </div>
      </div>
    </>
  );
};

// ── Address loader sub-component ─────────────────────────────
const AddressBlock = ({ addressId }) => {
  const [addr,    setAddr]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/user/addresses`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          const found = data.addresses.find(a => a.id === addressId);
          setAddr(found || null);
        }
      })
      .finally(() => setLoading(false));
  }, [addressId]);

  if (loading) return <p className="od-addr-loading">Loading address…</p>;
  if (!addr)   return <p className="od-addr-loading">Address not found</p>;

  return (
    <div className="od-addr">
      <p className="od-addr-name">{addr.full_name}</p>
      <p className="od-addr-line">{addr.phone}</p>
      <p className="od-addr-line">{addr.line_1}{addr.line_2 ? `, ${addr.line_2}` : ''}</p>
      <p className="od-addr-line">{addr.city}, {addr.state} – {addr.pincode}</p>
      {addr.label && <span className="od-addr-label">{addr.label}</span>}
    </div>
  );
};

// ── Styles ───────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;1,300&display=swap');

  .od-page {
    background: #FAF8F4;
    min-height: 100vh;
    padding-top: 140px;
    font-family: 'Jost', sans-serif;
    position: relative;
  }
  .od-page::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.018'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
  }

  /* Loading */
  .od-loading {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; min-height: 60vh; gap: 18px;
    position: relative; z-index: 1;
  }
  .od-loading-ring {
    width: 36px; height: 36px;
    border: 2px solid #e8e0d5; border-top-color: #B08D57;
    border-radius: 50%; animation: odSpin 0.9s linear infinite;
  }
  @keyframes odSpin { to { transform: rotate(360deg); } }
  .od-loading-text {
    font-family: 'Cormorant Garamond', serif;
    font-style: italic; font-size: 15px; color: #9A9186;
  }

  /* Error */
  .od-error {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; min-height: 60vh; gap: 12px;
    position: relative; z-index: 1; text-align: center;
  }
  .od-error-icon { font-size: 28px; color: #B08D57; margin: 0; }
  .od-error-msg  { font-size: 14px; color: '#C0392B'; }
  .od-back-link  {
    font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase;
    color: #B08D57; text-decoration: none; margin-top: 8px;
  }

  /* Hero */
  .od-hero {
    position: relative; z-index: 1;
    background: linear-gradient(170deg, #1C1A18 0%, #2C2820 100%);
    border-bottom: 1px solid rgba(176,141,87,0.2);
  }
  .od-hero-inner {
    max-width: 860px; margin: 0 auto;
    padding: 40px clamp(20px, 5vw, 56px) 36px;
  }
  .od-back {
    display: inline-flex; align-items: center; gap: 7px;
    font-size: 9px; font-weight: 600; letter-spacing: 0.2em;
    text-transform: uppercase; color: rgba(176,141,87,0.7);
    text-decoration: none; transition: color 0.2s; margin-bottom: 24px;
  }
  .od-back:hover { color: #B08D57; }
  .od-hero-content { margin-bottom: 26px; }
  .od-eyebrow {
    font-size: 9px; font-weight: 500; letter-spacing: 0.32em;
    text-transform: uppercase; color: #B08D57;
    display: flex; align-items: center; gap: 10px; margin-bottom: 8px;
  }
  .od-eyebrow::before { content: ''; width: 20px; height: 1px; background: #B08D57; }
  .od-order-num {
    font-family: 'Playfair Display', serif;
    font-size: clamp(26px, 4vw, 40px); font-weight: 700;
    color: #FAF8F4; letter-spacing: -0.01em; margin-bottom: 10px;
  }
  .od-date {
    font-size: 12px; color: rgba(250,248,244,0.45); letter-spacing: 0.06em;
  }
  .od-status-pill {
    display: inline-flex; align-items: center; gap: 7px;
    font-size: 10px; font-weight: 600; letter-spacing: 0.16em;
    text-transform: uppercase; padding: 8px 16px;
    border: 1px solid; border-radius: 100px;
  }

  /* Body */
  .od-body {
    position: relative; z-index: 1;
    max-width: 860px; margin: 0 auto;
    padding: 28px clamp(20px, 5vw, 56px) 80px;
    display: flex; flex-direction: column; gap: 16px;
  }

  /* Cards */
  .od-card {
    background: #fff;
    border: 1px solid rgba(176,141,87,0.12);
    border-radius: 2px;
    padding: 24px;
  }
  .od-card-title {
    font-family: 'Playfair Display', serif;
    font-size: 16px; font-weight: 700; color: #1C1A18;
    margin-bottom: 20px; letter-spacing: -0.01em;
    display: flex; align-items: center; gap: 8px;
  }
  .od-card-title-count {
    font-family: 'Jost', sans-serif;
    font-size: 10px; font-weight: 400; color: #8A7E72;
    letter-spacing: 0.06em; margin-left: 4px;
  }

  /* Timeline */
  .od-timeline {
    display: flex; align-items: flex-start; gap: 0;
    position: relative;
  }
  .od-timeline-step {
    display: flex; flex-direction: column; align-items: center;
    flex: 1; position: relative;
  }
  .od-tl-dot {
    width: 28px; height: 28px; border-radius: 50%;
    border: 2px solid #e0d8cc;
    background: #fff; display: flex; align-items: center;
    justify-content: center; position: relative; z-index: 2;
    transition: all 0.3s; flex-shrink: 0;
  }
  .od-tl-dot.done  { border-color: #B08D57; background: #B08D57; }
  .od-tl-dot.current { border-color: #B08D57; box-shadow: 0 0 0 4px rgba(176,141,87,0.15); }
  .od-tl-check { color: #fff; font-size: 11px; font-weight: 700; }
  .od-tl-line {
    position: absolute; top: 14px; left: 50%; width: 100%;
    height: 2px; background: #e0d8cc; z-index: 1;
    transition: background 0.3s;
  }
  .od-tl-line.done { background: #B08D57; }
  .od-tl-label {
    font-size: 10px; font-weight: 500; letter-spacing: 0.08em;
    text-transform: uppercase; color: #AEA39A; margin-top: 10px;
    text-align: center; line-height: 1.4;
  }
  .od-tl-label.done { color: #1C1A18; }

  /* Items */
  .od-items { display: flex; flex-direction: column; gap: 0; }
  .od-item {
    display: grid; grid-template-columns: 80px 1fr auto;
    gap: 16px; padding: 16px 0; align-items: center;
    border-bottom: 1px solid rgba(176,141,87,0.08);
  }
  .od-item:last-child { border-bottom: none; padding-bottom: 0; }
  .od-item:first-child { padding-top: 0; }
  .od-item-img {
    width: 80px; height: 80px; border-radius: 2px;
    overflow: hidden; background: #F0EAE0; flex-shrink: 0;
    border: 1px solid rgba(176,141,87,0.1);
    display: flex; align-items: center; justify-content: center;
  }
  .od-item-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .od-item-name {
    font-size: 14px; font-weight: 500; color: #1C1A18;
    margin-bottom: 6px; line-height: 1.35; letter-spacing: 0.01em;
  }
  .od-item-meta {
    display: flex; gap: 10px; flex-wrap: wrap;
    font-size: 11px; color: #8A7E72; letter-spacing: 0.04em;
  }
  .od-item-meta span::before { content: '· '; opacity: 0.5; }
  .od-item-meta span:first-child::before { content: ''; }
  .od-item-price { text-align: right; flex-shrink: 0; }
  .od-item-unit {
    font-family: 'Playfair Display', serif;
    font-size: 15px; font-weight: 700; color: #B08D57;
  }
  .od-item-total {
    font-size: 11px; color: #AEA39A; margin-top: 2px; font-weight: 400;
  }

  /* Bottom row */
  .od-bottom-row {
    display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  }

  /* Summary */
  .od-summary-rows { display: flex; flex-direction: column; gap: 2px; }
  .od-summary-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 9px 0; font-size: 13px; color: #5C5247;
    border-bottom: 1px solid rgba(176,141,87,0.07);
  }
  .od-summary-row:last-child { border-bottom: none; }
  .od-summary-total {
    border-top: 1px solid rgba(176,141,87,0.18) !important;
    margin-top: 4px; padding-top: 14px !important;
  }
  .od-summary-total span:first-child {
    font-size: 11px; font-weight: 600; letter-spacing: 0.14em;
    text-transform: uppercase; color: #1C1A18;
  }
  .od-summary-total span:last-child {
    font-family: 'Playfair Display', serif;
    font-size: 20px; font-weight: 700; color: #B08D57;
  }
  .od-payment-tag {
    display: inline-flex; align-items: center; gap: 6px;
    margin-top: 14px; font-size: 10px; font-weight: 500;
    letter-spacing: 0.1em; text-transform: uppercase;
    color: #8A7E72; background: rgba(176,141,87,0.08);
    padding: 6px 12px; border-radius: 100px;
  }

  /* Address */
  .od-addr-loading { font-size: 12px; color: #9A9186; font-style: italic; }
  .od-addr-name {
    font-size: 14px; font-weight: 600; color: #1C1A18; margin-bottom: 6px;
  }
  .od-addr-line {
    font-size: 13px; color: #5C5247; margin-bottom: 3px;
    line-height: 1.55; font-weight: 300;
  }
  .od-addr-label {
    display: inline-block; margin-top: 10px;
    font-size: 9px; font-weight: 600; letter-spacing: 0.18em;
    text-transform: uppercase; color: #B08D57;
    background: rgba(176,141,87,0.1); padding: 4px 10px; border-radius: 100px;
  }

  /* Ornament */
  .od-ornament {
    display: flex; align-items: center; justify-content: center;
    gap: 16px; margin-top: 16px;
  }
  .od-orn-line {
    width: 80px; height: 1px;
    background: linear-gradient(to right, transparent, #B08D57);
  }
  .od-orn-line--r { background: linear-gradient(to left, transparent, #B08D57); }
  .od-orn-diamond {
    width: 6px; height: 6px; background: #B08D57;
    transform: rotate(45deg); flex-shrink: 0;
  }

  /* Responsive */
  @media (max-width: 640px) {
    .od-page        { padding-top: 110px; }
    .od-bottom-row  { grid-template-columns: 1fr; }
    .od-tl-label    { font-size: 8.5px; }
    .od-item        { grid-template-columns: 64px 1fr auto; gap: 12px; }
    .od-item-img    { width: 64px; height: 64px; }
  }
`;

export default OrderDetailPage;